/**
 * Offline Banner Component
 * Shows online/offline status, sync progress, and pending items
 * Uses SyncManager instead of Capacitor for browser compatibility
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw, Check, AlertTriangle, CloudOff, Cloud, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
    initSyncManager,
    syncAll,
    getSyncStatus,
    addSyncListener
} from '../services/SyncManager';
import { useLanguage } from '../contexts/LanguageContext';
import SyncDetailPanel from './SyncDetailPanel';
import './OfflineBanner.css';

const OfflineBanner = () => {
    const { t, language } = useLanguage();
    const isRTL = language === 'ar';

    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSyncing, setIsSyncing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
    const [showBanner, setShowBanner] = useState(false);
    const [lastSynced, setLastSynced] = useState(null);
    const [justSynced, setJustSynced] = useState(false);
    const [showDetailPanel, setShowDetailPanel] = useState(false);

    // Initialize sync manager and listen for events
    useEffect(() => {
        initSyncManager();

        const handleOnline = () => {
            setIsOnline(true);
            toast.success(t('backOnline') || 'ðŸŒ Back online');
        };

        const handleOffline = () => {
            setIsOnline(false);
            toast.warning(t('noInternet') || 'ðŸ“´ No internet connection');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Listen for sync events
        const unsubscribe = addSyncListener(handleSyncEvent);

        // Initial status check
        updateStatus();
        const interval = setInterval(updateStatus, 5000);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            unsubscribe();
            clearInterval(interval);
        };
    }, [t]);

    // Update status periodically
    const updateStatus = async () => {
        try {
            const status = await getSyncStatus();
            setIsOnline(status.isOnline);
            setIsSyncing(status.isSyncing);
            setPendingCount(status.totalPending || 0);
            setShowBanner(!status.isOnline || status.totalPending > 0 || status.isSyncing);
        } catch (e) {
            console.warn('[OfflineBanner] Status check failed:', e);
        }
    };

    // Handle sync events
    const handleSyncEvent = useCallback((event) => {
        switch (event.type) {
            case 'connection':
                setIsOnline(event.isOnline);
                break;
            case 'sync_start':
                setIsSyncing(true);
                setSyncProgress({ current: 0, total: 0 });
                break;
            case 'sync_progress':
                setSyncProgress({ current: event.current, total: event.total });
                break;
            case 'sync_complete':
                setIsSyncing(false);
                setPendingCount(0);
                setLastSynced(new Date());
                setJustSynced(true);
                setTimeout(() => setJustSynced(false), 3000);
                if (event.results.synced > 0) {
                    toast.success(`âœ… ${event.results.synced} ${t('itemsSynced') || 'items synced'}`);
                }
                if (event.results.failed > 0) {
                    toast.error(`âš ï¸ ${event.results.failed} ${t('itemsFailedSync') || 'items failed to sync'}`);
                }
                break;
            case 'sync_error':
                setIsSyncing(false);
                toast.error(t('syncFailed') || 'فشل المزامنة. سيعاد المحاولة.');
                break;
            default:
                break;
        }
    }, [t]);

    // Manual sync trigger
    const handleManualSync = async () => {
        if (isSyncing) return;
        await syncAll();
    };

    // Don't show if online with nothing pending and not syncing
    if (!showBanner && !justSynced) return null;

    return (
        <>
            <div
                className={`offline-banner ${isRTL ? 'rtl' : 'ltr'} ${!isOnline ? 'offline-banner--offline' :
                    isSyncing ? 'offline-banner--syncing' :
                        justSynced ? 'offline-banner--success' :
                            pendingCount > 0 ? 'offline-banner--pending' : ''
                    }`}
                onClick={() => setShowDetailPanel(true)}
                style={{ cursor: 'pointer' }}
            >
                <div className="offline-banner__content">
                    {/* Icon */}
                    <div className="offline-banner__icon">
                        {!isOnline ? (
                            <WifiOff className="h-5 w-5" />
                        ) : isSyncing ? (
                            <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : justSynced ? (
                            <Check className="h-5 w-5" />
                        ) : (
                            <CloudOff className="h-5 w-5" />
                        )}
                    </div>

                    {/* Message */}
                    <div className="offline-banner__text">
                        {!isOnline ? (
                            <>
                                <strong>{t('offlineMode') || 'وضع عدم الاتصال'}</strong>
                                {pendingCount > 0 && (
                                    <span className="offline-banner__count">
                                        {pendingCount} {t('pendingItems') || 'pending'}
                                    </span>
                                )}
                            </>
                        ) : isSyncing ? (
                            <>
                                <strong>{t('syncing') || 'جاري المزامنة...'}</strong>
                                {syncProgress.total > 0 && (
                                    <span className="offline-banner__count">
                                        {syncProgress.current}/{syncProgress.total}
                                    </span>
                                )}
                            </>
                        ) : justSynced ? (
                            <strong>{t('allSynced') || 'âœ“ All synced'}</strong>
                        ) : pendingCount > 0 ? (
                            <>
                                <strong>{pendingCount} {t('itemsPending') || 'items pending'}</strong>
                            </>
                        ) : null}
                    </div>

                    {/* "View Details" indicator */}
                    <ChevronDown className="h-4 w-4 opacity-60 ml-auto" />
                </div>

                {/* Sync button - show when online with pending items */}
                {isOnline && pendingCount > 0 && !isSyncing && (
                    <button
                        className="offline-banner__button"
                        onClick={(e) => { e.stopPropagation(); handleManualSync(); }}
                    >
                        <RefreshCw className="h-4 w-4" />
                        <span>{t('syncNow') || 'مزامنة الآن'}</span>
                    </button>
                )}

                {/* Progress bar when syncing */}
                {isSyncing && syncProgress.total > 0 && (
                    <div className="offline-banner__progress">
                        <div
                            className="offline-banner__progress-bar"
                            style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                        />
                    </div>
                )}
            </div>

            {/* Sync Detail Panel Overlay */}
            <SyncDetailPanel isOpen={showDetailPanel} onClose={() => setShowDetailPanel(false)} />
        </>
    );
};

export default OfflineBanner;

